import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import "../styles/PerformanceReport.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useTranslation } from "react-i18next";
import UsageReportData from "../services/UsageReportData";
import ReportHeader from "./ReportHeader";
import DetailedAccountStatusTable from "./DetailedAccountStatusTable";
import ServiceTable from "./ServiceTable";
import InactiveMASTable from "./InactiveMASTable";
import FinancialReportTable from "./FinancialReportTable";
import logo from "../assets/ms.png";
import Swal from "sweetalert2";

const MSILUsageReport = () => {
  const { t } = useTranslation();

  const [currentItems, setCurrentItems] = useState([]);

  // Date range state
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Report types
  const [reportType, setReportType] = useState("service_usage_report");

  // Data states
  const [backendServicesData, setBackendServicesData] = useState([]);
  const [backendInactiveMasData, setBackendInactiveMasData] = useState([]);
  const [
    backendDetailedAccountStatusData,
    setBackendDetailedAccountStatusData,
  ] = useState([]);

  const [filteredServicesData, setFilteredServicesData] = useState([]);
  const [filteredInactiveMasData, setFilteredInactiveMasData] = useState([]);
  const [
    filteredDetailedAccountStatusData,
    setFilteredDetailedAccountStatusData,
  ] = useState([]);
  const [backendFinancialData, setBackendFinancialData] = useState([]);
  const [filteredFinancialData, setFilteredFinancialData] = useState([]);
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterInactiveType, setFilterInactiveType] = useState("all");
  const [filterDetailedType, setFilterDetailedType] = useState("all");
  const [consumptionFilter, setConsumptionFilter] = useState("all");
  const [serviceSortConfig, setServiceSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [financialSortConfig, setFinancialSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const filterRef = useRef(null);

  // Onboarding balance cache — fetched once per session for MSIL admin,
  // then reused on every page change so onboardingReportRows is never re-fetched.
  const onboardingCacheRef = useRef(null);   // null = not yet fetched, Map = ready
  const onboardingFetchingRef = useRef(false); // prevents duplicate in-flight requests
  const [isOnboardingLoading, setIsOnboardingLoading] = useState(false);

  // Report data cache — keyed by `${reportType}:${page}`, so switching between
  // report-type tabs (or paging back and forth) reuses already-fetched data
  // instead of hitting the API again. Cleared only on a full page refresh.
  const reportDataCacheRef = useRef({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch report data based on report type
  const fetchReportData = async () => {
    const cacheKey = `${reportType}:${currentPage}`;
    const cachedEntry = reportDataCacheRef.current[cacheKey];

    // Only show the loading state when we actually need to hit the network —
    // a cache hit resolves synchronously below.
    setIsLoading(!cachedEntry);
    try {
      if (reportType === "service_usage_report") {
        let data, totalElements;
        if (cachedEntry) {
          ({ content: data, totalElements } = cachedEntry);
        } else {
          const paged = await UsageReportData.serviceUsageReport(currentPage - 1, itemsPerPage);
          data = paged?.content || [];
          totalElements = paged?.totalElements || data.length;
          reportDataCacheRef.current[cacheKey] = { content: data, totalElements };
        }
        setTotalItems(totalElements);
        setBackendServicesData(data);
        setFilteredServicesData(data);
      } else if (reportType === "inactive_mass_report") {
        let data, totalElements;
        if (cachedEntry) {
          ({ content: data, totalElements } = cachedEntry);
        } else {
          const paged = await UsageReportData.inactiveMasReport(currentPage - 1, itemsPerPage);
          data = paged?.content || [];
          totalElements = paged?.totalElements || data.length;
          reportDataCacheRef.current[cacheKey] = { content: data, totalElements };
        }
        setTotalItems(totalElements);
        setBackendInactiveMasData(data);
        setFilteredInactiveMasData(data);

      } else if (reportType === "financial_report") {
        // Sorting must apply across the whole report, not just the fetched page,
        // so once a sort is active we load the full dataset and paginate client-side.
        if (financialSortConfig.key) {
          const fullCacheKey = `${reportType}:full`;
          let fullData;
          const fullCached = reportDataCacheRef.current[fullCacheKey];
          if (fullCached) {
            fullData = fullCached.content;
          } else {
            const paged = await UsageReportData.financialReport();
            fullData = paged?.content || [];
            reportDataCacheRef.current[fullCacheKey] = { content: fullData };
          }
          setBackendFinancialData(fullData);
          const filtered = filterDataByDate(
            fullData,
            startDate,
            endDate,
            getFinancialDateFilterType(filterDetailedType)
          );
          const sorted = applyFinancialSort(filtered);
          setTotalItems(sorted.length);
          setFilteredFinancialData(sorted);
        } else {
          let data, totalElements;
          if (cachedEntry) {
            ({ content: data, totalElements } = cachedEntry);
          } else {
            const paged = await UsageReportData.financialReport(currentPage - 1, itemsPerPage);
            data = paged?.content || [];
            totalElements = paged?.totalElements || data.length;
            reportDataCacheRef.current[cacheKey] = { content: data, totalElements };
          }
          setTotalItems(totalElements);
          setBackendFinancialData(data || []);
          const filtered = filterDataByDate(
            data || [],
            startDate,
            endDate,
            getFinancialDateFilterType(filterDetailedType)
          );
          setFilteredFinancialData(filtered);
        }

      } else if (reportType === "detailed_account_status") {
        const role = sessionStorage.getItem("role");
        const isMSILAdmin = role === "MSIL" || role === "SUPER_ADMIN" || role === "ADMIN";

        if (isMSILAdmin) {
          // Cached balance map (or null if not yet ready) so the table
          // renders instantly without waiting for onboardingReportRows.
          const cachedMap = onboardingCacheRef.current; // Map or null

          let data, totalElements;
          if (cachedEntry) {
            ({ content: data, totalElements } = cachedEntry);
          } else {
            const paged = await UsageReportData.detailedAccountStatus(
              currentPage - 1,
              itemsPerPage,
              cachedMap  // if null → rows show null balances; if Map → merges inline
            );
            data = paged?.content || [];
            totalElements = paged?.totalElements || data.length;
            reportDataCacheRef.current[cacheKey] = { content: data, totalElements };
          }

          // Re-merge against the balance map here too, in case the map finished
          // loading after this page's content was first cached.
          const merged = cachedMap
            ? UsageReportData.mergeServicesWithBalances(data, cachedMap)
            : data;

          setTotalItems(totalElements);
          setBackendDetailedAccountStatusData(merged);
          setFilteredDetailedAccountStatusData(
            filterDataByDate(merged, startDate, endDate, filterDetailedType)
          );

          // Fetch onboarding balances in the background (once per session)
          if (!cachedMap && !onboardingFetchingRef.current) {
            onboardingFetchingRef.current = true;
            setIsOnboardingLoading(true);
            UsageReportData.fetchOnboardingBalances()
              .then((balanceMap) => {
                onboardingCacheRef.current = balanceMap;
                // Merge into whatever is currently displayed
                setBackendDetailedAccountStatusData((prev) => {
                  const mergedNow = UsageReportData.mergeServicesWithBalances(prev, balanceMap);
                  setFilteredDetailedAccountStatusData(
                    filterDataByDate(mergedNow, startDate, endDate, filterDetailedType)
                  );
                  return mergedNow;
                });
              })
              .catch((e) => console.warn("onboardingReportRows failed:", e))
              .finally(() => {
                setIsOnboardingLoading(false);
                onboardingFetchingRef.current = false;
              });
          }
        } else {
          // Non-admin: FundCheck is fast; use existing single-call path
          let data, totalElements;
          if (cachedEntry) {
            ({ content: data, totalElements } = cachedEntry);
          } else {
            const paged = await UsageReportData.detailedAccountStatus(currentPage - 1, itemsPerPage);
            data = paged?.content || [];
            totalElements = paged?.totalElements || data.length;
            reportDataCacheRef.current[cacheKey] = { content: data, totalElements };
          }
          setTotalItems(totalElements);
          setBackendDetailedAccountStatusData(data);
          setFilteredDetailedAccountStatusData(
            filterDataByDate(data, startDate, endDate, filterDetailedType)
          );
        }
      }

    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // financialSortConfig is included so that turning on/toggling the Financial
    // Report's Invoice Date sort triggers the full-dataset fetch needed to sort
    // across every page, not just the currently loaded one.
  }, [reportType, currentPage, financialSortConfig]);

  // Filter data by date
  const filterDataByDate = (data, startDate, endDate, type) => {
    if (!startDate || !endDate) {
      return data;
    }

    // Normalize to full-day boundaries so date-only values match as expected.
    const rangeStart = new Date(startDate);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);

    const parseDateSafe = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const inRange = (value) => {
      const parsed = parseDateSafe(value);
      return parsed ? parsed >= rangeStart && parsed <= rangeEnd : false;
    };

    return data.filter((item) => {
      switch (type) {
        case "order":
          return inRange(item.orderDate || item.invoiceDate);
        case "invoiceDate":
          return inRange(item.invoiceDate);
        case "release":
          return inRange(item.releaseDate);
        case "end":
          return inRange(item.endDate);
        case "plan":
          return inRange(item.planDate);
        default:
          return (
            inRange(item.orderDate) ||
            inRange(item.invoiceDate) ||
            inRange(item.releaseDate) ||
            inRange(item.endDate) ||
            inRange(item.planDate)
          );
      }
    });
  };

  // Financial Report's calendar filter should be based on Invoice Date by default
  // (the shared "all" case above also matches Plan Date, which is a different field).
  const getFinancialDateFilterType = (type) => (type === "all" ? "invoiceDate" : type);

  const normalizeConsumptionRatio = (value) => {
    if (value == null || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    // Backward compatibility: if backend ever sends percentage (0..100), normalize to 0..1.
    if (n > 1) return n / 100;
    if (n < 0) return 0;
    return n;
  };

  const resolveTotalConsumedRatio = (item) => {
    const candidates = [
      item?.totalConsumedPercentage,
      item?.totalConsumedPercent,
      item?.totalConsumption,
      item?.totalConsumptionRatio,
      item?.overallConsumption,
    ];
    for (const value of candidates) {
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return null;
  };

  const isDateSortKey = (key) =>
    key === "orderDate" ||
    key === "releaseDate" ||
    key === "lastConsumptionDate" ||
    key === "invoiceDate";

  const compareServiceRows = (a, b, key) => {
    const aVal = a?.[key];
    const bVal = b?.[key];

    if (isDateSortKey(key)) {
      const aTime = aVal ? new Date(aVal).getTime() : Number.NEGATIVE_INFINITY;
      const bTime = bVal ? new Date(bVal).getTime() : Number.NEGATIVE_INFINITY;
      return aTime - bTime;
    }

    const aNum = Number(aVal);
    const bNum = Number(bVal);
    const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
    if (bothNumeric) {
      return aNum - bNum;
    }

    return String(aVal ?? "").localeCompare(String(bVal ?? ""), undefined, {
      sensitivity: "base",
      numeric: true,
    });
  };

  const applyServiceSort = (rows) => {
    if (!serviceSortConfig.key) return rows;
    const sorted = [...rows].sort((a, b) =>
      compareServiceRows(a, b, serviceSortConfig.key)
    );
    return serviceSortConfig.direction === "desc" ? sorted.reverse() : sorted;
  };

  const handleServiceSort = (key) => {
    setServiceSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        // Date columns default to latest first (recent -> previous).
        direction: isDateSortKey(key) ? "desc" : "asc",
      };
    });
  };

  const applyFinancialSort = (rows) => {
    if (!financialSortConfig.key) return rows;
    const sorted = [...rows].sort((a, b) =>
      compareServiceRows(a, b, financialSortConfig.key)
    );
    return financialSortConfig.direction === "desc" ? sorted.reverse() : sorted;
  };

  const handleFinancialSort = (key) => {
    setFinancialSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction: isDateSortKey(key) ? "desc" : "asc",
      };
    });
  };

  const applyServiceFiltersAndSort = (rows) => {
    const filtered = (rows || []).filter((service) => {
      let dateCheck = true;
      if (startDate && endDate) {
        let dateToCheck;
        switch (filterType) {
          case "order":
            dateToCheck = service.orderDate;
            break;
          case "release":
            dateToCheck = service.releaseDate;
            break;
          case "end":
            dateToCheck = service.endDate;
            break;
          default:
            dateToCheck = service.orderDate || service.releaseDate || service.endDate;
        }
        if (dateToCheck) {
          const dateVal = new Date(dateToCheck);
          dateCheck = dateVal >= startDate && dateVal <= endDate;
        }
      }

      let consumptionCheck = true;
      if (consumptionFilter !== "all") {
        const ratio = normalizeConsumptionRatio(service.consumption);
        if (ratio == null) {
          consumptionCheck = false;
        } else if (consumptionFilter === "0-50") {
          consumptionCheck = ratio >= 0 && ratio <= 0.5;
        } else if (consumptionFilter === "50-80") {
          consumptionCheck = ratio > 0.5 && ratio <= 0.8;
        } else if (consumptionFilter === "80-100") {
          consumptionCheck = ratio > 0.8 && ratio <= 1;
        }
      }

      return dateCheck && consumptionCheck;
    });

    return applyServiceSort(filtered);
  };

  const applyFiltersForCurrentReport = (resetPage = false) => {
    if (reportType === "service_usage_report") {
      setFilteredServicesData(applyServiceFiltersAndSort(backendServicesData || []));
    } else if (reportType === "inactive_mass_report") {
      const filtered = filterDataByDate(backendInactiveMasData || [], startDate, endDate, filterInactiveType);
      setFilteredInactiveMasData(filtered);
    } else if (reportType === "detailed_account_status") {
      const filtered = filterDataByDate(backendDetailedAccountStatusData || [], startDate, endDate, filterDetailedType);
      setFilteredDetailedAccountStatusData(filtered);
    } else if (reportType === "financial_report") {
      const filtered = filterDataByDate(
        backendFinancialData || [],
        startDate,
        endDate,
        getFinancialDateFilterType(filterDetailedType)
      );
      const sorted = applyFinancialSort(filtered);
      // In sorted (full-dataset) mode, backendFinancialData holds every row, so the
      // filtered/sorted count is the true total for pagination.
      if (financialSortConfig.key) {
        setTotalItems(sorted.length);
      }
      setFilteredFinancialData(sorted);
    }
    if (resetPage) {
      // after explicitly applying filters, ensure we start on page 1
      setCurrentPage(1);
    }
  };

  // Apply filters and update content on explicit Apply click
  const handleApply = () => {
    applyFiltersForCurrentReport(true);
  };

  // Auto-apply currently selected date/filter values when switching reports or data refreshes.
  // This keeps date-range behavior consistent across report tabs without forcing another Apply click.
  useEffect(() => {
    applyFiltersForCurrentReport(false);
  }, [
    reportType,
    startDate,
    endDate,
    filterType,
    filterInactiveType,
    filterDetailedType,
    consumptionFilter,
    serviceSortConfig,
    financialSortConfig,
    backendServicesData,
    backendInactiveMasData,
    backendDetailedAccountStatusData,
    backendFinancialData,
  ]);

  // Reset to page 1 whenever filters change (keeps safety)
  useEffect(() => {
    setCurrentPage(1);
  }, [
    startDate,
    endDate,
    reportType,
    filterType,
    filterInactiveType,
    filterDetailedType,
    consumptionFilter,
    serviceSortConfig,
    financialSortConfig,
  ]);

  // === NEW: compute currentItems (paginated) based on reportType & currentPage ===
  const getCurrentItems = () => {
    if (reportType === "service_usage_report") {
      return filteredServicesData || [];
    } else if (reportType === "inactive_mass_report") {
      return filteredInactiveMasData || [];
    } else if (reportType === "detailed_account_status") {
      return filteredDetailedAccountStatusData || [];
    } else if (reportType === "financial_report") {
      if (financialSortConfig.key) {
        // Sorted mode holds the entire report in filteredFinancialData, so slice
        // out just the current page here instead of relying on backend pagination.
        const start = (currentPage - 1) * itemsPerPage;
        return (filteredFinancialData || []).slice(start, start + itemsPerPage);
      }
      return filteredFinancialData || [];
    }
    return [];
  };

  useEffect(() => {
    setCurrentItems(getCurrentItems());
  }, [
    currentPage,
    filteredServicesData,
    filteredInactiveMasData,
    filteredDetailedAccountStatusData,
    filteredFinancialData,
    financialSortConfig,
    reportType,
  ]);

  // Keep a compatibility alias used previously in your exports
  const currentServices = currentItems; // <-- current page items for service report

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getAllRowsForExport = async () => {
    if (reportType === "service_usage_report" || reportType === "usage_reports") {
      const allData = await UsageReportData.serviceUsageReport();
      return applyServiceFiltersAndSort(allData?.content || []);
    }

    if (reportType === "inactive_mass_report") {
      const allData = await UsageReportData.inactiveMasReport();
      return filterDataByDate(
        allData?.content || [],
        startDate,
        endDate,
        filterInactiveType
      );
    }

    if (reportType === "detailed_account_status") {
      // Pass cached balance map so export avoids re-fetching onboardingReportRows
      // when it was already loaded for the table view.
      const allData = await UsageReportData.detailedAccountStatus(
        undefined,
        undefined,
        onboardingCacheRef.current ?? undefined
      );
      return filterDataByDate(
        allData?.content || [],
        startDate,
        endDate,
        filterDetailedType
      );
    }

    if (reportType === "financial_report") {
      const allData = await UsageReportData.financialReport();
      const filtered = filterDataByDate(
        allData?.content || [],
        startDate,
        endDate,
        getFinancialDateFilterType(filterDetailedType)
      );
      return applyFinancialSort(filtered);
    }

    return [];
  };

  // Pagination rendering fix
  const renderPagination = () => {
    const totalPages = Math.ceil((totalItems || 0) / itemsPerPage);
    if (totalPages <= 1) return null;

    const pageNumbers = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      if (currentPage <= 3) {
        pageNumbers.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pageNumbers.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination" style={{ justifyContent: "center" }}>
          {/* Prev button */}
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
            >
              {"<"}
            </button>
          </li>

          {/* Page numbers */}
          {pageNumbers.map((number, index) => (
            <li
              key={index}
              className={`page-item ${currentPage === number ? "active" : ""} ${number === "..." ? "disabled" : ""
                }`}
            >
              {number === "..." ? (
                <span className="page-link">...</span>
              ) : (
                <button className="page-link" onClick={() => paginate(number)}>
                  {number}
                </button>
              )}
            </li>
          ))}

          {/* Next button */}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
            >
              {">"}
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  const handleConsumptionFilterChange = (e) =>
    setConsumptionFilter(e.target.value);
  const handleFilterClick = () => setIsFilterVisible(!isFilterVisible);

  // ================= Export Functions =================
  const prepareExportData = (data, reportType) => {
    return data.map((item) => {
      const baseData = {
        [t("mass_name_label")]: item.massName,
        //  [t("mobile_label")]: item.mobile,
        [t("region_label")]: item.region,
        [t("service_name_label")]: item.serviceName,
        [t("plan_name_label")]: item.planName,
        [t("mass_code_label")]: item.massCode,
        [t("status_label")]: item.status,
        [t("order_date_label")]: item.orderDate
          ? new Date(item.orderDate).toLocaleDateString()
          : "",
        [t("release_date_label")]: item.releaseDate
          ? new Date(item.releaseDate).toLocaleDateString()
          : "",
        [t("end_date_label")]: item.endDate
          ? new Date(item.endDate).toLocaleDateString()
          : "",
        [t("invoice_number_label")]: item.invoiceNumber || "",
        [t("price_label")]: item.price,
        [t("auto_debit_label")]: item.autoDebit
          ? ["yes", "true"].includes(item.autoDebit.toLowerCase())
            ? t("yes")
            : t("no")
          : "-",
      };

      if (reportType === "inactive_mass_report") {
        baseData["Post Invoice Status"] =
          item.dmsPostInvoicesStatus === "1"
            ? "Pushed"
            : item.dmsPostInvoicesStatus === "0"
            ? "Failed"
            : "-";
        baseData["Post Invoice Push Timestamp"] = item.dmsPostInvoicesTimestamp
          ? new Date(item.dmsPostInvoicesTimestamp).toLocaleString()
          : "-";
        baseData["Auto Debit Repurchase"] = item.autoDebitRepurchase || "-";
        baseData["InsertMass Push Status"] =
          item.insertMassPushStatus === "1"
            ? "Pushed"
            : item.insertMassPushStatus === "0"
            ? "Failed"
            : "-";
        baseData["InsertMass Push Timestamp"] = item.insertMassPushTimestamp
          ? new Date(item.insertMassPushTimestamp).toLocaleString()
          : "-";
        baseData["SAP Push Status"] =
          item.sapPushStatus === "1"
            ? "Confirmed"
            : item.sapPushStatus === "0"
            ? "Failed"
            : "-";
        baseData["SAP Push Timestamp"] = item.sapPushTimestamp
          ? new Date(item.sapPushTimestamp).toLocaleString()
          : "-";
      }

      if (reportType === "usage_reports") {
        baseData[t("usage_label")] = item.usage;
        baseData[t("balance_label")] = item.balance;
      }
      if (reportType === "service_usage_report") {
        baseData["Minutes Purchased"] = item.minutesPurchased ?? "";
        baseData["Messages Purchased"] = item.messagesPurchased ?? "";
        baseData["Last Minutes Consumed"] = item.consumedMinutes ?? "";
        baseData["Last Messages Consumed"] = item.consumedMessages ?? "";
        baseData["Last Consumption Date"] = item.lastConsumptionDate
          ? new Date(item.lastConsumptionDate).toLocaleDateString()
          : "";
        baseData["Last Consumption %"] =
          item.consumption !== undefined &&
            item.consumption !== null &&
            !Number.isNaN(Number(item.consumption))
            ? `${(Number(item.consumption) > 1 ? Number(item.consumption) : Number(item.consumption) * 100).toFixed(2)}%`
            : "";
        baseData["Total Consumed Minutes"] = item.totalConsumedMinutes ?? "";
        baseData["Total Consumed Messages"] = item.totalConsumedMessages ?? "";
        const totalConsumed = resolveTotalConsumedRatio(item);
        baseData["Total Consumed %"] =
          totalConsumed !== undefined &&
          totalConsumed !== null &&
          !Number.isNaN(Number(totalConsumed))
            ? `${(Number(totalConsumed) > 1 ? Number(totalConsumed) : Number(totalConsumed) * 100).toFixed(2)}%`
            : "";
      }

      if (reportType === "detailed_account_status") {
        baseData[t("account_balance_label")] =
          item.accountBalance !== undefined &&
          item.accountBalance !== null &&
          !Number.isNaN(Number(item.accountBalance))
            ? Number(item.accountBalance).toFixed(2)
            : "N/A";
        baseData[t("account_balance_timestamped_label")] =
          item.accountBalanceTimestamp
            ? new Date(item.accountBalanceTimestamp).toLocaleString()
            : "N/A";
        baseData[t("no_of_active_services_label")] =
          item.activeServicesCount !== undefined && item.activeServicesCount !== null
            ? item.activeServicesCount
            : "N/A";
      }
      
      if (reportType === "financial_report") {
        return {
          [t("financial_report_mass_code")]: item.massCode,
          [t("financial_report_mass_name")]: item.massName,
          [t("financial_report_service_name")]: item.serviceName,
          [t("plan_name_label")]: item.planName,
          [t("status_label")]: item.status,
          [t("financial_report_invoice_number")]: item.invoiceNumber,
          [t("financial_report_invoice_date")]: item.invoiceDate ? new Date(item.invoiceDate).toLocaleDateString() : "",
          [t("financial_report_basic_amount")]: item.basicAmount,
          [t("financial_report_igst_amount")]: item.igstAmount,
          [t("financial_report_sgst_amount")]: item.sgstAmount,
          [t("financial_report_cgst_amount")]: item.cgstAmount,
          [t("financial_report_total_amount")]: item.totalAmount,
          [t("financial_report_gst_number")]: item.gstNumberOfMass,
          [t("financial_report_plan_date")]: item.planDate ? new Date(item.planDate).toLocaleDateString() : "",
          [t("financial_report_minutes_provided")]: item.minutesProvided,
          [t("financial_report_messages_provided")]: item.messagesProvided,
          "Auto Debit Repurchase": item.autoDebitRepurchase || "-",
        };
      }

      return baseData;
    });
  };

  const exportToExcel = async () => {
    let exportData = [];
    let sheetName = "";
    let fileName = "";
    const allRows = await getAllRowsForExport();

    switch (reportType) {
      case "usage_reports":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = prepareExportData(allRows, reportType);
        sheetName = t("usage_report_sheet_name");
        fileName = t("usage_report_filename");
        break;
      case "service_usage_report":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = prepareExportData(allRows, reportType);
        sheetName = t("services_report_sheet_name") || "Services Report";
        fileName = t("services_report_filename") || "services_report";
        break;
      case "inactive_mass_report":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = prepareExportData(allRows, reportType);
        sheetName = t("inactive_mASS_report") || "Inactive MASS Report";
        fileName = t("inactive_mASS_report_filename") || "inactive_mass_report";
        break;
      case "detailed_account_status":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = prepareExportData(allRows, reportType);
        sheetName = t("mass_account_status") || "Detailed Account Status";
        fileName =
          t("mass_account_status_filename") || "detailed_account_status";
        break;
        case "financial_report":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = prepareExportData(allRows, reportType);
        sheetName = t("financial_report_sheet_name") || "Financial Report";
        fileName = t("financial_report_filename") || "financial_report";
        break;
      default:
        return;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName + ".xlsx");
  };

  const exportToPdf = async () => {
    let exportData = [];
    let head = [];
    let title = "";
    let fileName = "";
    const allRows = await getAllRowsForExport();

    switch (reportType) {
      case "usage_reports":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = allRows.map((row) => [
          row.massName,
          // row.mobile,
          row.region,
          row.serviceName,
          row.planName,
          row.massCode,
          row.status,
          row.orderDate ? row.orderDate.toLocaleDateString() : "",
          row.releaseDate ? row.releaseDate.toLocaleDateString() : "",
          row.endDate ? row.endDate.toLocaleDateString() : "",
          row.usage,
          row.balance,
          row.price,
          row.autoDebit && ["yes", "true"].includes(row.autoDebit.toLowerCase()) ? t("yes") : t("no"),
        ]);
        head = [
          [
            t("mass_name_label"),
            //  t("mobile_label"),
            t("region_label"),
            t("service_name_label"),
            t("plan_name_label"),
            t("mass_code_label"),
            t("status_label"),
            t("order_date_label"),
            t("release_date_label"),
            t("end_date_label"),
            t("usage_label"),
            t("balance_label"),
            t("price_label"),
            t("auto_debit_label"),
          ],
        ];
        title = t("usage_report_title");
        fileName = t("usage_report_filename");
        break;

      case "service_usage_report":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = allRows.map((row) => [
          row.massName,
          // row.mobile,
          row.region,
          row.serviceName,
          row.planName,
          row.massCode,
          row.status,
          row.orderDate ? new Date(row.orderDate).toLocaleDateString() : "",
          row.releaseDate ? new Date(row.releaseDate).toLocaleDateString() : "",
          row.endDate ? new Date(row.endDate).toLocaleDateString() : "",
          row.invoiceNumber || "",
          row.minutesPurchased ?? "",
          row.messagesPurchased ?? "",
          row.consumedMinutes ?? "",
          row.consumedMessages ?? "",
          row.lastConsumptionDate ? new Date(row.lastConsumptionDate).toLocaleDateString() : "",
          row.consumption !== undefined &&
            row.consumption !== null &&
            !Number.isNaN(Number(row.consumption))
            ? `${(Number(row.consumption) > 1 ? Number(row.consumption) : Number(row.consumption) * 100).toFixed(2)}%`
            : "",
          row.totalConsumedMinutes ?? "",
          row.totalConsumedMessages ?? "",
          resolveTotalConsumedRatio(row) !== undefined &&
            resolveTotalConsumedRatio(row) !== null &&
            !Number.isNaN(Number(resolveTotalConsumedRatio(row)))
            ? `${(Number(resolveTotalConsumedRatio(row)) > 1
              ? Number(resolveTotalConsumedRatio(row))
              : Number(resolveTotalConsumedRatio(row)) * 100).toFixed(2)}%`
            : "",
          row.price,
          row.autoDebit
            ? ["yes", "true"].includes(row.autoDebit.toLowerCase())
              ? t("yes")
              : t("no")
            : "-",
        ]);

        head = [
          [
            "MASS Name",
            //    "Mobile",
            "Region",
            "Service Name",
            "Plan Name",
            "MASS Code",
            "Status",
            "Order Date",
            "Release Date",
            "End Date",
            "Invoice Number",
            "Minutes Purchased",
            "Messages Purchased",
            "Last Minutes Consumed",
            "Last Messages Consumed",
            "Last Consumption Date",
            "Last Consumption %",
            "Total Consumed Minutes",
            "Total Consumed Messages",
            "Total Consumed %",
            "Price",
            "Auto Debit",
          ],
        ];
        title = "Service Usage Report";
        fileName = t("services_report_filename") || "services_report";
        break;

      case "inactive_mass_report":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = allRows.map((row) => [
          row.massName,
          //  row.mobile,
          row.region,
          row.serviceName,
          row.planName,
          row.massCode,
          row.status,
          row.orderDate ? new Date(row.orderDate).toLocaleDateString() : "",
          row.releaseDate ? new Date(row.releaseDate).toLocaleDateString() : "",
          row.endDate ? new Date(row.endDate).toLocaleDateString() : "",
          row.invoiceNumber || "",
          row.price,
          row.autoDebit && ["yes", "true"].includes(row.autoDebit.toLowerCase()) ? t("yes") : t("no"),
          row.dmsPostInvoicesStatus === "1"
            ? "Pushed"
            : row.dmsPostInvoicesStatus === "0"
            ? "Failed"
            : "-",
          row.dmsPostInvoicesTimestamp
            ? new Date(row.dmsPostInvoicesTimestamp).toLocaleString()
            : "-",
          row.autoDebitRepurchase || "-",
          row.insertMassPushStatus === "1"
            ? "Pushed"
            : row.insertMassPushStatus === "0"
            ? "Failed"
            : "-",
          row.insertMassPushTimestamp
            ? new Date(row.insertMassPushTimestamp).toLocaleString()
            : "-",
          row.sapPushStatus === "1"
            ? "Confirmed"
            : row.sapPushStatus === "0"
            ? "Failed"
            : "-",
          row.sapPushTimestamp
            ? new Date(row.sapPushTimestamp).toLocaleString()
            : "-",
        ]);
        head = [
          [
            "MASS Name",
            //      "Mobile",
            "Region",
            "Service Name",
            "Plan Name",
            "MASS Code",
            "Status",
            "Order Date",
            "Release Date",
            "End Date",
            t("invoice_number_label"),
            "Price",
            "Auto Debit",
            "Post Invoice Status",
            "Post Invoice Push Timestamp",
            "Auto Debit Repurchase",
            "InsertMass Push Status",
            "InsertMass Push Timestamp",
            "SAP Push Status",
            "SAP Push Timestamp",
          ],
        ];
        title = "Inactive MASS Report";
        fileName = t("inactive_mASS_report_filename") || "inactive_mass_report";
        break;

      case "detailed_account_status":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = allRows.map((row) => [
          row.massName,
          //  row.mobile,
          row.region,
          row.serviceName,
          row.planName,
          row.massCode,
          row.status,
          row.orderDate ? new Date(row.orderDate).toLocaleDateString() : "",
          row.releaseDate ? new Date(row.releaseDate).toLocaleDateString() : "",
          row.endDate ? new Date(row.endDate).toLocaleDateString() : "",
          row.price,
          row.autoDebit && ["yes", "true"].includes(row.autoDebit.toLowerCase()) ? t("yes") : t("no"),
          row.accountBalance !== undefined &&
          row.accountBalance !== null &&
          !Number.isNaN(Number(row.accountBalance))
            ? Number(row.accountBalance).toFixed(2)
            : "N/A",
          row.accountBalanceTimestamp
            ? new Date(row.accountBalanceTimestamp).toLocaleString()
            : "N/A",
          row.activeServicesCount,
        ]);
        head = [
          [
            "MASS Name",
            //  "Mobile",
            "Region",
            "Service Name",
            "Plan Name",
            "MASS Code",
            "Status",
            "Order Date",
            "Release Date",
            "End Date",
            "Price",
            "Auto Debit",
            "Account Balance",
            "Account Balance (Timestamped)",
            "No. of Active Services",
          ],
        ];
        title = "Detailed Account Status";
        fileName =
          t("mass_account_status_filename") || "detailed_account_status";
        break;

        case "financial_report":
        if (!allRows.length) {
          Swal.fire({
            icon: "warning",
            title: t("no_data_export_message"),
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
        }
        exportData = allRows.map((row) => [
          row.massCode,
          row.massName,
          row.serviceName,
          row.planName,
          row.status,
          row.invoiceNumber,
          row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString() : "",
          row.basicAmount,
          row.igstAmount,
          row.sgstAmount,
          row.cgstAmount,
          row.totalAmount,
          row.gstNumberOfMass,
          row.planDate ? new Date(row.planDate).toLocaleDateString() : "",
          row.minutesProvided,
          row.messagesProvided,
          row.autoDebitRepurchase || "-",
        ]);
        head = [
          [
            t("financial_report_mass_code"),
            t("financial_report_mass_name"),
            t("financial_report_service_name"),
            t("plan_name_label"),
            t("status_label"),
            t("financial_report_invoice_number"),
            t("financial_report_invoice_date"),
            t("financial_report_basic_amount"),
            t("financial_report_igst_amount"),
            t("financial_report_sgst_amount"),
            t("financial_report_cgst_amount"),
            t("financial_report_total_amount"),
            t("financial_report_gst_number"),
            t("financial_report_plan_date"),
            t("financial_report_minutes_provided"),
            t("financial_report_messages_provided"),
            "Auto Debit Repurchase",
          ],
        ];
        title = "Financial Report";
        fileName = t("financial_report_filename") || "financial_report";
        break;
      default:
        return;
    }

    const doc = new jsPDF("l", "pt", "a4");

    const img = new Image();
    img.src = logo;
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.addImage(img, "PNG", 25, 30, 120, 20);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const textX = (pageWidth - textWidth) / 2;
    doc.text(title, textX, 45);

    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.line(30, 60, pageWidth - 30, 60);

    doc.autoTable({
      head,
      body: exportData,
      startY: 70,
      styles: {
        fontSize: 9,
        cellPadding: 5,
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        halign: "center",
        valign: "middle",
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      theme: "grid",
    });

    doc.save(fileName + ".pdf");
  };

  // ====================================================

  return (
    <div className="usage-report">
      <ReportHeader
        reportType={reportType}
        setReportType={setReportType}
        onApply={handleApply}
        onFilterClick={handleFilterClick}
      />

      {/* Date Range & Filters */}
      <div
        className="date-range-container"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          backgroundColor: "#e0e0e0",
          padding: "10px",
          paddingTop: 0,
          paddingBottom: "15px",
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={setDateRange}
            isClearable
            dateFormat={t("date_format")}
            maxDate={new Date()}
            placeholderText={t("select_date_range")}
          />
          <button className="apply-btn" onClick={handleApply}>
            {t("apply_button")}
          </button>
          
          {(reportType === "financial_report" || reportType === "detailed_account_status") && (
            <select
              className="report-type-dropdown"
              value={filterDetailedType}
              onChange={(e) => setFilterDetailedType(e.target.value)}
              style={{ marginLeft: "5px" }}
            >
              <option value="all">{t("all_dates")}</option>
              <option value="order">{reportType === "financial_report" ? t("financial_report_invoice_date") : t("order_date_label")}</option>
              <option value="plan">{t("financial_report_plan_date")}</option>
              <option value="release">{t("release_date_label")}</option>
              <option value="end">{t("end_date_label")}</option>
            </select>
          )}

          {reportType === "service_usage_report" && (
            <div
              style={{ position: "relative", display: "inline-block" }}
              onMouseEnter={() => setIsFilterVisible(true)}
              onMouseLeave={() => setIsFilterVisible(false)}
            >
              <button className="filter-btn" style={{ marginLeft: "10px" }}>
                <FontAwesomeIcon icon={faFilter} />
              </button>
              {isFilterVisible && (
                <div
                  ref={filterRef}
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    zIndex: 100,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    padding: "10px",
                    marginTop: "5px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "bold",
                      color: "#333",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    {t("consumption_range")}
                  </label>
                  <select
                    id="consumption-filter"
                    className="report-type-dropdown"
                    value={consumptionFilter}
                    onChange={handleConsumptionFilterChange}
                    style={{ marginBottom: "10px" }}
                  >
                    <option value="all">{t("all")}</option>
                    <option value="0-50">0 - 50%</option>
                    <option value="50-80">50 - 80%</option>
                    <option value="80-100">80 - 100%</option>
                  </select>

                  <label
                    style={{
                      fontWeight: "bold",
                      color: "#333",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    {t("filter_by_date")}
                  </label>
                  <select
                    id="filter-type"
                    className="report-type-dropdown"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">{t("all_dates")}</option>
                    <option value="order">{t("order_date_label")}</option>
                    <option value="release">{t("release_date_label")}</option>
                    <option value="end">{t("end_date_label")}</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Export Buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button
              className="export-btn"
              onClick={exportToExcel}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                padding: "10px 15px",
                border: "1px solid green",
                borderRadius: "5px",
                backgroundColor: "#f0f9f0",
                color: "green",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "background-color 0.3s ease, color 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "green";
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#f0f9f0";
                e.target.style.color = "green";
              }}
            >
              {t("export_to_excel")}
              <i className="fas fa-file-excel" style={{ fontSize: "18px" }}></i>
            </button>

            <button
              className="export-btn export-btn-pdf"
              onClick={exportToPdf}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                padding: "10px 15px",
                border: "1px solid red",
                borderRadius: "5px",
                backgroundColor: "#fff0f0",
                color: "red",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "background-color 0.3s ease, color 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "red";
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff0f0";
                e.target.style.color = "red";
              }}
            >
              {t("export_to_pdf")}
              <i className="fas fa-file-pdf" style={{ fontSize: "18px" }}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {/* Render paginated tables using currentItems (keeps prop names same as original) */}
      {reportType === "service_usage_report" && (
        <ServiceTable
          services={currentItems}
          sortConfig={serviceSortConfig}
          onSort={handleServiceSort}
        />
      )}
      {reportType === "inactive_mass_report" && (
        <InactiveMASTable inactiveMasData={currentItems} />
      )}
      {reportType === "detailed_account_status" && (
        <DetailedAccountStatusTable
          detailedData={currentItems}
          isOnboardingLoading={isOnboardingLoading}
        />
      )}
      {reportType === "financial_report" && (
        <FinancialReportTable
          financialData={currentItems}
          sortConfig={financialSortConfig}
          onSort={handleFinancialSort}
        />
      )}
      {/* Pagination */}
      <div
        style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}
      >
        {renderPagination()}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <span>{t("loading_message")}</span>
        </div>
      )}
    </div>
  );
};

export default MSILUsageReport;
